import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { usePageTitle } from './utils/pageTitle'
import { useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
// Kept eager: these are the public/first-paint routes (landing + auth entry
// points). Lazy-loading them would add a network round trip to the exact
// pages we need fastest, so they stay in the main bundle.
import LandingPage from './pages/LandingPage'
import RequestAccessPage from './pages/RequestAccessPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import LoadingSpinner from './components/LoadingSpinner'
import RouteErrorBoundary from './components/RouteErrorBoundary'

// Everything below is route-level code-split (Perf: route code splitting).
// Verified none of these page components are imported anywhere other than
// this file (grep across src/), so each import() below is a genuine,
// independent chunk boundary.
const SetPasswordPage = lazy(() => import('./pages/auth/SetPasswordPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ModelUploadPage = lazy(() => import('./pages/models/ModelUploadPage'))
const EnhancedModelUpload = lazy(() => import('./pages/models/EnhancedModelUpload'))
const ModelsPage = lazy(() => import('./pages/models/ModelsPage'))
const ModelDetailPage = lazy(() => import('./pages/models/ModelDetailPage'))
const ModelPredictionResults = lazy(() => import('./pages/models/ModelPredictionResults'))
const SchemaPage = lazy(() => import('./pages/schema/SchemaPage'))
const TablesPage = lazy(() => import('./pages/schema/TablesPage'))
const MigrationsPage = lazy(() => import('./pages/schema/MigrationsPage'))
const DataImportPage = lazy(() => import('./pages/data/DataImportPage'))
const DataBrowserPage = lazy(() => import('./pages/data/DataBrowserPage'))
// Unified self-service Account surface (Profile/Security/Preferences tabs)
// — formerly ProfilePage, at /app/profile. That URL still works: it
// redirects below instead of 404ing for old bookmarks/links.
const AccountPage = lazy(() => import('./pages/AccountPage'))
const ImageManager = lazy(() => import('./pages/ImageManager'))
const VisualItemCreator = lazy(() => import('./pages/analytics/VisualItemCreator'))
const SavedVisuals = lazy(() => import('./pages/analytics/SavedVisuals'))
const RuntimeSettingsPage = lazy(() => import('./pages/settings/RuntimeSettingsPage'))
const ModelRunsSettingsPage = lazy(() => import('./pages/settings/ModelRunsSettingsPage'))
// Notebook + Settings + Admin (RBAC) — Task #15 / Commit 6c.1b
const NotebookManager = lazy(() => import('./pages/models/NotebookManager'))
const NotebookRunDetail = lazy(() => import('./pages/models/NotebookRunDetail'))
// ViewModelsPage + NotebookRunDetail (v1) were superseded by the v2 design
// on 2026-06-18 — kept on disk as a safety net only. To revert, swap the
// imports/routes below. Once the v2 design is locked in for ~1 week, delete
// the v1 files.
const RegistryPageV2 = lazy(() => import('./pages/models/v2/RegistryPage'))
const ModelAccuracyLeaderboard = lazy(() => import('./pages/models/ModelAccuracyLeaderboard'))
const AlertsPage = lazy(() => import('./pages/alerts/AlertsPage'))
const IngestReviewPage = lazy(() => import('./pages/ingest/IngestReviewPage'))
const AlertsConfigPage = lazy(() => import('./pages/alerts/AlertsConfigPage'))
// Forecast Severity Map — 416-sector WHO-threshold choropleth (ANALYTICS_
// REDESIGN.md Surface 2), a sibling of Alerts, not nested under Analytics/
// Visual Creator: it's a fixed surveillance instrument, not a build-your-
// own visual.
// Restored 2026-08-14: the RBC/MoH meeting PDFs list this map as an explicit
// client action item (monthly Rwanda forecast maps, WHO-standard legend,
// rolling next 6 months). The 2026-08-12 hide was based on a wrong "nobody
// asked" read; corrected.
const ForecastSeverityMapPage = lazy(() => import('./pages/analytics/ForecastSeverityMapPage'))
const RunDetailPageV2 = lazy(() => import('./pages/models/v2/RunDetailPage'))
const LandingContentEditor = lazy(() => import('./pages/settings/LandingContentEditor'))
const BrandingSettingsPage = lazy(() => import('./pages/settings/BrandingSettingsPage'))
// Alert-notification settings GUI (PR #43, Track B).
const AlertNotificationsPage = lazy(() => import('./pages/settings/AlertNotificationsPage'))
// External Integrations Registry GUI — over the already-shipped, security-
// reviewed /api/integrations backend.
const IntegrationsRegistryPage = lazy(() => import('./pages/settings/IntegrationsRegistryPage'))
// Integration-type catalog admin (migration 0027) — the data-driven
// type/field-schema catalog IntegrationsRegistryPage's own type picker and
// credential fields are fetched from.
const IntegrationTypesPage = lazy(() => import('./pages/settings/IntegrationTypesPage'))
// DHIS2 management — org-unit + data-element mapping, on-demand pull/push,
// for one dhis2-typed registry row. Backend already shipped (routes/dhis2.js,
// docs/DHIS2.md); this is the GUI over it, reached via the registry's
// "Manage" action on a dhis2 row.
const Dhis2ManagementPage = lazy(() => import('./pages/settings/Dhis2ManagementPage'))
const UsersPanel = lazy(() => import('./pages/admin/UsersPanel'))
const AccessRequestsPanel = lazy(() => import('./pages/admin/AccessRequestsPanel'))
// RBAC de-hardcode Slice 2 — super-admin console over the Slice-1 API
// (services/auth/routes/rbac.js). Same 'people' section, manage_users gate.
const RolesConsolePage = lazy(() => import('./pages/admin/RolesConsolePage'))
// Settings consolidation (Task #8) — shared shell + section landing.
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout'))
const SettingsIndexRedirect = lazy(() => import('./pages/settings/SettingsIndexRedirect'))
// SystemOverviewGate wraps AdminPanel with the route-level permission
// check added in QA follow-up (2026-07-20) — see SystemOverviewGate.jsx.
// AdminPanel itself is imported (eagerly, same chunk) from inside that
// file, not lazy-loaded separately here.
const SystemOverviewGate = lazy(() => import('./pages/settings/SystemOverviewGate'))
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'))

// Full-page fallback — identical markup to the existing auth-loading screen
// below (same bg-gray-50 + centered LoadingSpinner). Used for lazy routes
// that render standalone (outside <Layout>), where nothing else is on
// screen yet, so this exactly matches what the user already saw a moment
// earlier during the auth check. No color/background mismatch, no flash.
const fullPageFallback = (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
)

// Content-area fallback — used for lazy routes nested inside <Layout>.
// Layout (sidebar + header) is eager and already on screen, so the
// fallback must NOT take over the full viewport (that would blank the
// sidebar/header and cause a flash on every in-app navigation). It only
// fills the content area, with the same bg-gray-50 the <main> already has,
// so there is no color flash — just a spinner in place of the page.
const contentFallback = (
  <div className="min-h-[60vh] flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
)

// withFallback: wraps a lazy element in its own Suspense boundary so
// suspending one route never blanks a sibling/parent (e.g. Layout's
// sidebar+header must stay mounted while a nested page chunk loads).
//
// The error boundary sits OUTSIDE the Suspense deliberately. React.lazy throws a
// promise while loading (which Suspense catches) but throws an Error when the
// fetch fails, and that propagates past Suspense to the nearest boundary above it
// — inside, it would never fire. One boundary per route rather than one around
// <Routes>, so a failed chunk degrades that route's content area instead of
// unmounting Layout.
//
// The key is load-bearing, and an earlier version of this without it was wrong.
// Every sibling route renders its element into the same slot under Layout's
// <Outlet/>, and React reconciles by position and type, not by which call site
// produced the element — so navigating between routes REUSES this boundary
// instance rather than remounting it, and a caught error survives the navigation.
// The observable failure was ugly: one 404ing chunk left every other route in the
// layout stuck showing that first error, so a single stale chunk poisoned
// navigation to healthy pages. Keying on pathname forces a remount per route,
// which clears the error state.
function RouteBoundary({ fallback, children }) {
  const location = useLocation()

  return (
    <RouteErrorBoundary key={location.pathname}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </RouteErrorBoundary>
  )
}

const withFallback = (element, fallback = contentFallback) => (
  <RouteBoundary fallback={fallback}>{element}</RouteBoundary>
)

function App() {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()
  usePageTitle()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <Routes>
      {/* Landing page - public route.
          Wrapped like every other route: these two are eagerly imported, so
          the Suspense half is inert, but the error boundary is not. Without
          it a render-time throw anywhere in the landing tree propagated to
          the root and unmounted the app — the public site went white with no
          recovery, which is the worst possible failure surface since it's the
          first thing a visitor sees. Now it degrades to RouteErrorBoundary's
          fallback. */}
      <Route path="/" element={withFallback(<LandingPage />, fullPageFallback)} />

      {/* Public access-request form (the "Request Access" CTA on the
          landing page links here). POSTs to /api/public/access-request. */}
      <Route path="/request-access" element={withFallback(<RequestAccessPage />, fullPageFallback)} />

      {/* Legacy /image-manager — Task #8 (settings consolidation).
          This used to mount ImageManager directly, standalone, with no
          auth check at all (it sat outside the /app protected route
          tree). ImageManager now lives only at /app/settings/content/images,
          which requires isAuthenticated same as every other Settings page.
          This redirect keeps the old bookmark working; it also closes the
          pre-existing gap of the editor UI being reachable while logged
          out (the underlying POST /api/public/images endpoint still has
          no server-side auth check of its own — see services/auth/routes/public.js
          and services/api.js — that's unrelated backend work, flagged, not
          fixed here). */}
      <Route path="/image-manager" element={<Navigate to="/app/settings/content/images" replace />} />

      {/* Password setup — public route reached via email link (Task #48).
          Token in the URL is validated by the page on mount. No auth
          required — these are by definition users who CAN'T log in yet. */}
      <Route path="/set-password/:token" element={withFallback(<SetPasswordPage />, fullPageFallback)} />

      {/* Self-service password reset (Task #68). User submits email,
          backend issues a password_setup_tokens row with purpose='reset'
          and emails a /set-password/<token> link — same consume flow as
          the welcome path. */}
      <Route path="/forgot-password" element={withFallback(<ForgotPasswordPage />, fullPageFallback)} />

      {/* Public routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <LoginPage />
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <RegisterPage />
        }
      />

      {/* Protected routes. An unauthenticated visitor is bounced to /login
          carrying the route they were trying to reach in `state.from`, so
          LoginPage can send them back there after they authenticate instead
          of always landing on the dashboard. */}
      <Route
        path="/app"
        element={
          isAuthenticated
            ? <Layout />
            : <Navigate to="/login" replace state={{ from: location }} />
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={withFallback(<DashboardPage />)} />
        <Route path="account" element={withFallback(<AccountPage />)} />
        {/* Legacy /app/profile — redirect, don't 404 (same convention as
            the /app/admin* redirects below). */}
        <Route path="profile" element={<Navigate to="/app/account" replace />} />
        {/* Anomaly alerts (Task #7) — sector incidence > 24mo p90/p95/p99 */}
        <Route path="alerts" element={withFallback(<AlertsPage />)} />
        <Route path="ingest" element={withFallback(<IngestReviewPage />)} />

        {/* Forecast Severity Map (restored 2026-08-14) — client-requested map
            of forecast alert severity by sector, WHO-standard legend. */}
        <Route path="forecast-map" element={withFallback(<ForecastSeverityMapPage />)} />

        {/* Model Intelligence routes — notebook-driven.
            /app/models             → registry (browse + recent runs)
            /app/models/notebooks   → NotebookManager (upload + analyze)
            /app/models/notebooks/runs/:runId → NotebookRunDetail (PNG + Excel)
            Legacy joblib routes still callable by direct URL only. */}
        <Route path="models">
          {/* v2 design is now canonical (Task #70 + #71 promoted from
              parallel to default on 2026-06-18). */}
          <Route index element={withFallback(<RegistryPageV2 />)} />
          <Route path="notebooks" element={withFallback(<NotebookManager />)} />
          {/* IMPORTANT: RunDetailPage reads useParams().run_id (snake_case).
              The route param must match exactly. */}
          <Route path="notebooks/runs/:run_id" element={withFallback(<RunDetailPageV2 />)} />
          {/* Real predicted-vs-observed accuracy leaderboard — must come
              before the `:modelId` catch-all below or "accuracy" would be
              swallowed as a model id param. */}
          <Route path="accuracy" element={withFallback(<ModelAccuracyLeaderboard />)} />
          {/* Legacy joblib routes — not in nav, accessible by direct URL */}
          <Route path="legacy" element={withFallback(<ModelsPage />)} />
          <Route path="upload" element={withFallback(<EnhancedModelUpload />)} />
          <Route path="enhanced-upload" element={withFallback(<EnhancedModelUpload />)} />
          <Route path="upload-legacy" element={withFallback(<ModelUploadPage />)} />
          <Route path=":modelId" element={withFallback(<ModelDetailPage />)} />
          <Route path=":modelId/predictions" element={withFallback(<ModelPredictionResults />)} />
        </Route>

        {/* =============================================================
            Settings — unified workspace, config only (Settings IA rebuild,
            2026-07; builds on the original Task #8 settings consolidation).
            SettingsLayout is the shared shell: a real two-level in-page
            nav (L1 section bar — Content/People/Maps & Locale/Alerts — plus
            an L2 page sub-nav for whichever section is active). Every page
            under it is untouched, only relocated; permission gates are
            unchanged from the taxonomy in sections.js.

            Two operational tools that used to live here under a 'system'
            section — the AdminPanel health dashboard and the
            AlertsConfigPage detector tuning — are NOT here anymore: they
            don't belong in a configuration workspace. They're relocated to
            top-level routes below (/app/system, /app/alerts/detector). This
            is what fixed the old shell's orphan bug too: every remaining
            page here now sits under a real section with a real L1+L2 tab,
            including Alert Notifications (used to be a flat, tab-less
            /app/settings/alert-notifications route). */}
        <Route path="settings" element={withFallback(<SettingsLayout />)}>
          <Route index element={withFallback(<SettingsIndexRedirect />)} />

          {/* Content — landing page copy + marketing images/roster */}
          <Route path="content">
            <Route index element={<Navigate to="landing-content" replace />} />
            <Route path="landing-content" element={withFallback(<LandingContentEditor />)} />
            <Route path="images" element={withFallback(<ImageManager />)} />
            <Route path="branding" element={withFallback(<BrandingSettingsPage />)} />
          </Route>

          {/* People — user accounts + signup approval queue */}
          <Route path="people">
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={withFallback(<UsersPanel />)} />
            <Route path="roles" element={withFallback(<RolesConsolePage />)} />
            <Route path="access-requests" element={withFallback(<AccessRequestsPanel />)} />
          </Route>

          {/* Maps & Locale — single page; it has its OWN internal
              Boundary/Presentation/Localization tab strip
              (RuntimeSettingsPage.jsx), left untouched by this rebuild. */}
          <Route path="runtime" element={withFallback(<RuntimeSettingsPage />)} />

          {/* Models — run-worker pool config (concurrency, resource-aware
              hold thresholds, failed-run cleanup). GUI over migration 0034 +
              runController.js run-settings API. manage_settings / manage_models
              gated both here and server-side. */}
          <Route path="models">
            <Route index element={<Navigate to="runs" replace />} />
            <Route path="runs" element={withFallback(<ModelRunsSettingsPage />)} />
          </Route>

          {/* Alerts — notification routing/schedule config (PR #43, Track B
              — builds on Track A's /api/alerts/notifications contract).
              manage_alert_notifications gated both here (page-level
              canManage check) and server-side. Not to be confused with
              Alert Detector (WHO seasonal-month profile tuning), which is
              an operational tool at the top-level /app/alerts/detector,
              outside Settings entirely. */}
          <Route path="alerts">
            <Route index element={<Navigate to="notifications" replace />} />
            <Route path="notifications" element={withFallback(<AlertNotificationsPage />)} />
          </Route>

          {/* Integrations — External Integrations Registry (DHIS2, SMTP,
              SMS gateways, generic REST APIs). Backend already shipped +
              passed security QA (/api/integrations, manage_integrations
              gated server-side); this is the admin GUI over it. */}
          <Route path="integrations">
            <Route index element={<Navigate to="registry" replace />} />
            <Route path="registry" element={withFallback(<IntegrationsRegistryPage />)} />
            {/* The type/field-schema catalog itself (migration 0027) —
                separate admin surface from the registry above, same
                manage_integrations gate. */}
            <Route path="types" element={withFallback(<IntegrationTypesPage />)} />
            {/* DHIS2 management detail page — one per dhis2-typed registry
                row, reached via that row's "Manage" action. */}
            <Route path="dhis2/:id" element={withFallback(<Dhis2ManagementPage />)} />
          </Route>

          {/* Legacy leaf URLs — redirect, don't 404.
              landing-content / images predate the original Task #8
              consolidation. alert-notifications / system / system/alerts-config
              predate THIS rebuild (they were the consolidation-era paths). */}
          <Route path="landing-content" element={<Navigate to="/app/settings/content/landing-content" replace />} />
          <Route path="images" element={<Navigate to="/app/settings/content/images" replace />} />
          <Route path="alert-notifications" element={<Navigate to="/app/settings/alerts/notifications" replace />} />
          <Route path="system" element={<Navigate to="/app/system" replace />} />
          <Route path="system/alerts-config" element={<Navigate to="/app/alerts/detector" replace />} />
        </Route>

        {/* System Overview — relocated OUT of Settings by the Settings IA
            rebuild (an operational health dashboard, not configuration).
            Still wrapped by SystemOverviewGate (manage_users OR
            approve_signups) — the route moved, the gate didn't. */}
        <Route path="system" element={withFallback(<SystemOverviewGate />)} />

        {/* Audit log — super-admin security console over audit_schema.audit_logs.
            Self-gates on view_audit_logs (redirects otherwise); the read API is
            server-gated the same way (services/auth/routes/audit.js). */}
        <Route path="audit" element={withFallback(<AuditLogPage />)} />

        {/* Alert Detector — relocated OUT of Settings, now a sibling of
            /app/alerts (the anomaly list it tunes) instead of nested three
            levels deep under Settings/System. Same page, same permission
            (manage_users), only the URL and its place in the nav changed. */}
        <Route path="alerts/detector" element={withFallback(<AlertsConfigPage />)} />

        {/* Old /app/admin* URLs — redirect, don't 404. Existing links and
            bookmarks (including the post-login admin redirect in
            LoginPage.jsx, updated to the new path directly) must keep
            working. Kept inside the authenticated /app tree so the access
            level is identical to before — nothing here is reachable
            without being logged in, same as pre-consolidation. */}
        <Route path="admin" element={<Navigate to="/app/system" replace />} />
        <Route path="admin/users" element={<Navigate to="/app/settings/people/users" replace />} />
        <Route path="admin/access-requests" element={<Navigate to="/app/settings/people/access-requests" replace />} />
        <Route path="admin/alerts-config" element={<Navigate to="/app/alerts/detector" replace />} />

        {/* Dynamic Schema routes */}
        <Route path="schema">
          <Route index element={withFallback(<SchemaPage />)} />
          <Route path="tables" element={withFallback(<TablesPage />)} />
          <Route path="migrations" element={withFallback(<MigrationsPage />)} />
        </Route>

        {/* Data Management routes */}
        <Route path="data">
          <Route path="import" element={withFallback(<DataImportPage />)} />
          <Route path="browse" element={withFallback(<DataBrowserPage />)} />
        </Route>

        {/* Analytics routes */}
        <Route path="analytics">
          <Route path="visual-creator" element={withFallback(<VisualItemCreator />)} />
          <Route path="saved-visuals" element={withFallback(<SavedVisuals />)} />
          <Route path="map-boundaries" element={<Navigate to="/app/settings/runtime" replace />} />
        </Route>
      </Route>

      {/* Catch all route */}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/app/dashboard" : "/"} replace />}
      />
    </Routes>
  )
}

export default App
